import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Button, Drawer, Modal } from "antd";
import getUserLocation from "../../utils/location";
import { showAlertModal } from "../alertModal";
import constants from "../../utils/constants";
import { useRouter } from "next/router";
import { useUserLocation } from "../Location/LocationContext";
import axios from "axios";
import { GET_ALL_CITIES } from "../../utils/queries";
import { formatCityName, getCanonicalCitySlug } from "../../config/cityData";

interface SelectCityProps {
  setCity?: Dispatch<SetStateAction<string>>;
  visible?: boolean;
  onClose?: () => void;
  onLoadingChange?: (loading: boolean) => void;
  onCitySelect?: (citySlug: string) => void | Promise<void>;
}

interface City {
  id: string;
  name: string;
}

const TURF_URL = process.env.NEXT_PUBLIC_TURF_URL ?? "";

const SelectLocation = ({
  setCity,
  onClose,
  visible = false,
  onLoadingChange,
  onCitySelect,
}: SelectCityProps = {}) => {
  const router = useRouter();
  const [isMobile, setIsMobile] = useState(false);
  const [storedCity, setStoredCity] = useState<string | undefined>(undefined);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [isCityPickerOpen, setIsCityPickerOpen] = useState(false);
  const safeSetCity = setCity || (() => undefined);
  const safeOnClose = onClose || (() => undefined);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCity = localStorage.getItem("selectedCity");
      const normalizedCity = savedCity ? getCanonicalCitySlug(savedCity) : undefined;
      setStoredCity(normalizedCity);
      setSelectedCity(normalizedCity);
      
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 767);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, []);

  const { userLocation, setUserLocation } = useUserLocation();

  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!visible || typeof window === "undefined") return;

    const savedCity = localStorage.getItem("selectedCity");
    const normalizedCity = savedCity ? getCanonicalCitySlug(savedCity) : undefined;
    setStoredCity(normalizedCity);
    setSelectedCity(normalizedCity);
    setCitySearch("");
    setIsCityPickerOpen(false);
  }, [visible]);

  const fetchCities = async () => {
    try {
      setLoading(true);
      onLoadingChange?.(true);
      const response = await axios.post(TURF_URL + `/graphql`, {
        query: GET_ALL_CITIES,
        variables: {},
      });
      setCities(response.data.data.cities);
      setLoading(false);
      onLoadingChange?.(false);
    } catch (err) {
      setError("Failed to fetch cities");
      setLoading(false);
      onLoadingChange?.(false);
    }
  };

  const submitCitySelection = useCallback(
    async (value?: string) => {
      const fallbackCity = getCanonicalCitySlug(constants.DEFAULT_CITY);
      const citySlug = getCanonicalCitySlug(value || storedCity || fallbackCity);

      setIsSubmitting(true);
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem("selectedCity", citySlug);
        }
        setStoredCity(citySlug);
        setSelectedCity(citySlug);
        safeSetCity(() => citySlug);
        setUserLocation(null);

        if (onCitySelect) {
          await onCitySelect(citySlug);
        } else {
          const cityPath = `/${citySlug}`;
          await router.push(cityPath);
        }

        safeOnClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCitySelect, router, safeOnClose, safeSetCity, setUserLocation, storedCity]
  );

  const getCurrentLocation = () => {
    setIsSubmitting(true);

    const handleLocationSet = async (location: any) => {
      setUserLocation(location);
      const detectedCity = getCanonicalCitySlug(location?.city || constants.DEFAULT_CITY);
      setSelectedCity(detectedCity);
      await submitCitySelection(detectedCity);
    };

    const handleLocationError = async () => {
      const defaultCity = getCanonicalCitySlug(constants.DEFAULT_CITY);
      setSelectedCity(defaultCity);
      await submitCitySelection(defaultCity);
      showAlertModal({ isMobile })();
    };

    getUserLocation(handleLocationSet, handleLocationError);
  };

  const handleCityChange = (value: string) => {
    setSelectedCity(getCanonicalCitySlug(value));
    setCitySearch("");
    setIsCityPickerOpen(false);
  };

  const handleClose = useCallback(() => {
    if (onCitySelect) {
      safeOnClose();
      return;
    }

    // Don't redirect if we're already on /noData page to avoid loops
    if (router.pathname === '/noData' || router.asPath === '/noData') {
      safeOnClose();
      return;
    }
    
    // Only redirect to home if we have a valid city
    const storedCity = typeof window !== 'undefined' ? localStorage.getItem("selectedCity") : null;
    if (storedCity) {
      router.push(`/${getCanonicalCitySlug(storedCity)}`);
    } else {
      router.push("/");
    }
    safeOnClose();
  }, [onCitySelect, router.pathname, router.asPath, router, safeOnClose]);

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Drawer is portaled under body — not inside wrapperRef; ignore those clicks
    // or mousedown here would run handleClose before the city option click fires.
    if (target.closest(".ant-drawer")) {
      return;
    }

    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(target) &&
      typeof window !== "undefined" &&
      !!localStorage.getItem("selectedCity")
    ) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleEvent = () => {
      if (visible) {
        document.addEventListener("mousedown", handleOutsideClick);
      } else {
        document.removeEventListener("mousedown", handleOutsideClick);
      }
    };
    handleEvent();
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [visible, handleOutsideClick]);

  useEffect(() => {
    fetchCities();
  }, []);

  const selectOptions = useMemo(
    () =>
      [...cities]
        .filter((city) => city?.id && city?.name)
        .sort((a, b) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()))
        .map((city) => ({
          value: getCanonicalCitySlug(city.name),
          label: formatCityName(city.name),
        })),
    [cities]
  );

  const activeCity = selectedCity || storedCity || getCanonicalCitySlug(constants.DEFAULT_CITY);
  const filteredCityOptions = useMemo(() => {
    const normalizedQuery = citySearch.trim().toLowerCase();
    if (!normalizedQuery) return selectOptions;

    return selectOptions.filter((option) => {
      const label = option.label.toLowerCase();
      const value = option.value.toLowerCase();
      return label.includes(normalizedQuery) || value.includes(normalizedQuery);
    });
  }, [citySearch, selectOptions]);

  const handleCityInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCitySearch(event.target.value);
    setIsCityPickerOpen(true);
  };

  const handleCityInputFocus = () => {
    setIsCityPickerOpen(true);
  };

  const handleCityToggle = () => {
    if (isCityPickerOpen) {
      setIsCityPickerOpen(false);
      return;
    }

    if (isMobile) {
      setCitySearch("");
    }

    setIsCityPickerOpen(true);

    if (isMobile) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          mobileSearchInputRef.current?.focus();
        });
      });
      return;
    }

    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
  };

  const handleCityInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && filteredCityOptions[0]) {
      event.preventDefault();
      handleCityChange(filteredCityOptions[0].value);
      return;
    }

    if (event.key === "Escape") {
      setIsCityPickerOpen(false);
    }
  };

  // if (loading) {
  //   return <Spin size="large" />;
  // }

  if (error) {
    return <div>{error}</div>;
  }

  const cityListContent = loading ? (
    <div className="city-picker__status">Loading cities...</div>
  ) : filteredCityOptions.length ? (
    filteredCityOptions.map((option) => {
      const isActive = option.value === activeCity;
      return (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={isActive}
          className={`city-picker__option${isActive ? " is-active" : ""}`}
          onClick={() => handleCityChange(option.value)}
        >
          <span>{option.label}</span>
          {isActive && <small>Selected</small>}
        </button>
      );
    })
  ) : (
    <div className="city-picker__status">No matching city found</div>
  );

  return (
    <div>
      <Modal
        title={
          <div className="select-location-modal__title">
            <span className="select-location-modal__eyebrow">Choose your city</span>
            <h2>Pick where you want to play</h2>
            <p>Select a city to explore venues, courts and sport-specific listings.</p>
          </div>
        }
        open={visible}
        footer={null}
        centered
        closeIcon={storedCity ? undefined : null}
        onCancel={handleClose}
        width={isMobile ? "calc(100vw - 24px)" : 540}
        wrapClassName="select-location-modal"
        maskClosable={false}
      >
        <div ref={wrapperRef} className="select-location-modal__body">
          <Button onClick={getCurrentLocation} className="select-location-current" loading={isSubmitting}>
            <div className="select-location-current__content">
              <div className="select-location-current__icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  aria-labelledby="icon-svg-title- icon-svg-desc-"
                  role="img"
                >
                  <title>current-location</title>
                  <path
                    d="M13.58 10c0 1.977-1.603 3.58-3.58 3.58s-3.58-1.603-3.58-3.58c0-1.977 1.603-3.58 3.58-3.58v0c1.977 0 3.58 1.603 3.58 3.58v0zM20 9.52v0.96c0 0.265-0.215 0.48-0.48 0.48v0h-1.72c-0.447 3.584-3.256 6.393-6.802 6.836l-0.038 0.004v1.72c0 0.265-0.215 0.48-0.48 0.48v0h-0.96c-0.265 0-0.48-0.215-0.48-0.48v0-1.72c-3.575-0.455-6.375-3.262-6.816-6.802l-0.004-0.038h-1.74c-0.265 0-0.48-0.215-0.48-0.48v0-0.96c0-0.265 0.215-0.48 0.48-0.48v0h1.74c0.445-3.578 3.245-6.385 6.781-6.836l0.039-0.004v-1.72c0-0.265 0.215-0.48 0.48-0.48v0h0.96c0.265 0 0.48 0.215 0.48 0.48v0 1.72c3.584 0.447 6.393 3.256 6.836 6.802l0.004 0.038h1.72c0.265 0 0.48 0.215 0.48 0.48v0zM15.96 10c0-3.292-2.668-5.96-5.96-5.96s-5.96 2.668-5.96 5.96c0 3.292 2.668 5.96 5.96 5.96v0c3.292 0 5.96-2.668 5.96-5.96v0z"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                </svg>
              </div>
              <div className="select-location-current__text">
                <strong>Use current location</strong>
                <span>Detect your city automatically using GPS</span>
              </div>
            </div>
          </Button>

          {isMobile ? (
            <div className="city-dropdown city-dropdown--mobile">
              <div className="select-location-section-heading">
                <span>Select city</span>
              </div>
              <button
                type="button"
                className={`select-location-mobile-city-row${isCityPickerOpen ? " is-open" : ""}`}
                onClick={handleCityToggle}
                aria-expanded={isCityPickerOpen}
                aria-haspopup="dialog"
              >
                <div className="select-location-mobile-city-row__main">
                  <span className="select-location-mobile-city-row__label">Your city</span>
                  <span className="select-location-mobile-city-row__value">{formatCityName(activeCity)}</span>
                </div>
                <span className="select-location-mobile-city-row__action">
                  {isCityPickerOpen ? "Close" : "Change"}
                  <svg viewBox="0 0 20 20" aria-hidden="true" className="select-location-mobile-city-row__chev">
                    <path
                      d="M5 7.5 10 12.5 15 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>
          ) : (
            <div className="city-dropdown">
              <div className="select-location-section-heading">
                <span>Select city</span>
              </div>
              <div className={`city-picker${isCityPickerOpen ? " is-open" : ""}`}>
                <div className="city-picker__control">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={citySearch}
                    className="city-picker__input"
                    onChange={handleCityInputChange}
                    onFocus={handleCityInputFocus}
                    onKeyDown={handleCityInputKeyDown}
                    placeholder="Search for a city..."
                    name="tb-city-search-desktop"
                    id="tb-city-search-desktop"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    inputMode="search"
                    enterKeyHint="search"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={isCityPickerOpen}
                    aria-controls="select-location-city-listbox-desktop"
                  />
                  <button
                    type="button"
                    className="city-picker__toggle"
                    onClick={handleCityToggle}
                    aria-label={isCityPickerOpen ? "Close city list" : "Open city list"}
                  >
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path
                        d="M5 7.5 10 12.5 15 7.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {isCityPickerOpen && (
                  <div className="city-picker__panel" id="select-location-city-listbox-desktop" role="listbox">
                    {cityListContent}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="select-location-footer">
            <div className="select-location-footer__selected">
              <span>Selected city</span>
              <strong>{formatCityName(activeCity)}</strong>
            </div>
            <Button
              type="primary"
              className="select-location-submit"
              block
              loading={isSubmitting}
              onClick={() => submitCitySelection(activeCity)}
            >
              Continue to {formatCityName(activeCity)}
            </Button>
          </div>
        </div>
      </Modal>

      <Drawer
        rootClassName="select-location-city-drawer"
        placement="bottom"
        open={visible && isMobile && isCityPickerOpen}
        onClose={() => setIsCityPickerOpen(false)}
        zIndex={1100}
        closable={false}
        destroyOnClose
        styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
        maskStyle={{ background: "rgba(15, 10, 35, 0.52)" }}
      >
        <div className="select-location-sheet">
          <div className="select-location-sheet__chrome" aria-hidden="true">
            <span className="select-location-sheet__grab" />
          </div>
          <div className="select-location-sheet__header">
            <h3 className="select-location-sheet__title">Select city</h3>
            <button
              type="button"
              className="select-location-sheet__close"
              onClick={() => setIsCityPickerOpen(false)}
              aria-label="Close city list"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="select-location-sheet__search">
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={citySearch}
              className="city-picker__input city-picker__input--sheet"
              onChange={handleCityInputChange}
              onFocus={handleCityInputFocus}
              onKeyDown={handleCityInputKeyDown}
              placeholder="Search for a city..."
              name="tb-city-search-sheet"
              id="tb-city-search-sheet"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="search"
              enterKeyHint="search"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={isCityPickerOpen}
              aria-controls="select-location-city-listbox"
            />
          </div>
          <div
            id="select-location-city-listbox"
            className="select-location-sheet__list"
            role="listbox"
          >
            {cityListContent}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default SelectLocation;
